import { useEffect } from 'react';
import coreAxios from 'src/utils/core-axios';

// Converts the VAPID public key from URL-safe Base64 to a Uint8Array
// required by the PushManager subscribe() API
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function registerPush(userId, userType, token, companyId) {
  try {
    // Fetch the VAPID public key from the backend
    const { data: { publicKey } } = await coreAxios.get('/api/core/notifications/vapid-public-key', {
      headers: { Authtoken: token },
    });
    if (!publicKey) return;

    // Register the service worker
    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    const newAppKey = urlBase64ToUint8Array(publicKey);

    // If an existing subscription is bound to an old/different VAPID key, unsubscribe it first
    if (subscription) {
      const currentKey = subscription.options?.applicationServerKey;
      let keyMismatch = false;
      if (currentKey) {
        const currentArr = new Uint8Array(currentKey);
        if (currentArr.length !== newAppKey.length || !currentArr.every((val, i) => val === newAppKey[i])) {
          keyMismatch = true;
        }
      } else {
        keyMismatch = true;
      }

      if (keyMismatch) {
        console.log('[Push] Stale/Mismatched VAPID key detected, re-subscribing...');
        await subscription.unsubscribe().catch(() => {});
        subscription = null;
      }
    }

    if (!subscription) {
      // Ask the user for notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('[Push] Notification permission denied');
        return;
      }

      // Subscribe with the VAPID key
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: newAppKey,
      });
    }

    // Save the subscription in the backend DB
    await coreAxios.post(
      '/api/core/notifications/subscribe',
      { userId, userType, companyId, subscription },
      { headers: { Authtoken: token } }
    );

    console.log('[Push] Subscription registered successfully');

    // Pattern A — fire one summary push for missed notifications on login
    await coreAxios.post(
      '/api/core/notifications/summary-push',
      { userId, userType },
      { headers: { Authtoken: token } }
    ).catch(() => {}); // non-critical
  } catch (err) {
    console.error('[Push] Registration failed:', err);
  }
}

/**
 * Hook to register the browser for Web Push notifications.
 * Automatically requests permission and saves the subscription to the backend.
 *
 * @param {string|number} userId  - The logged-in user's ID
 * @param {string} userType       - The user's role type (e.g. 'admin')
 * @param {string} token          - The auth token for API calls
 * @param {string|number} companyId - The logged-in user's company ID
 */
export function usePushNotifications(userId, userType, token, companyId) {
  useEffect(() => {
    if (!userId || !userType || !token || !companyId) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[Push] This browser does not support push notifications');
      return;
    }

    registerPush(userId, userType, token, companyId);
  }, [userId, userType, token, companyId]);
}
